import { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  ErpDataGrid,
  ErpMasterShell
} from '../../../components/common/erp-grid';
import { buildOtherChargeColumns } from './otherChargesColumns';
import { otherChargesApi, loadOtherChargesDropdownsSafe } from './otherChargesApi';
import OtherChargeRuleModal from './OtherChargeRuleModal';
import { normalizeOtherChargeRule } from './otherChargesHelpers';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Other Charges Master page (Stage 6OC.1 · ERP-grid aligned).
//
// Mirrors the CustomerMasterPage / SupplierMasterPage shape so this
// master feels identical to the rest of the ERP:
//   - Reusable <ErpDataGrid /> inside <ErpMasterShell /> (compact rows,
//     sticky header, toolbar search / export / column show-hide, ⋯ row
//     actions, paginator).
//   - A dedicated centered form popup (OtherChargeRuleModal — same
//     chrome family as SupplierModal) for Add / Edit instead of the
//     grid's built-in modal: passing `onAdd` / `onEdit` makes the grid
//     hand off to our popup. Delete uses the grid's built-in confirm.
//
// Other Charges' list endpoint returns the full rule set in one call,
// so the grid runs in CLIENT mode — `dataSource` is the normalised
// rule array and the grid does its own search / sort / filter /
// pagination. Master-driven dropdowns (material / glass / thickness /
// category) feed both the popup and the Material-Type column filter;
// they are NEVER hardcoded.
// ─────────────────────────────────────────────────────────────────

function backendError(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback || 'Something went wrong.';
}

export default function OtherChargesMasterPage() {
  // ── rule list (client-mode dataSource) ──────────────────────────
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── master-driven dropdowns ─────────────────────────────────────
  const [dropdowns, setDropdowns] = useState({
    materialTypes: [], glassTypes: [], glassFamilies: [], thicknesses: [], categories: [], polishTypes: [], gstRates: [], errors: null
  });
  const [dropdownsLoading, setDropdownsLoading] = useState(false);

  // ── popup state ─────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [modalRecord, setModalRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ── loaders ─────────────────────────────────────────────────────
  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await otherChargesApi.listRules({ limit: 500, offset: 0 });
      const rows = data && Array.isArray(data.rows) ? data.rows : (Array.isArray(data) ? data : []);
      setRules(rows.map((r) => normalizeOtherChargeRule(r)));
    } catch (err) {
      message.error({ content: backendError(err, 'Failed to load charge rules.'), key: 'oc-list' });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDropdowns = useCallback(async () => {
    setDropdownsLoading(true);
    try {
      const data = await loadOtherChargesDropdownsSafe();
      setDropdowns({
        materialTypes: data.materialTypes || [], glassTypes: data.glassTypes || [],
        glassFamilies: data.glassFamilies || [],
        thicknesses: data.thicknesses || [], categories: data.categories || [],
        polishTypes: data.polishTypes || [],
        gstRates: data.gstRates || [], errors: data.errors || null
      });
    } finally {
      setDropdownsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRules();
    loadDropdowns();
  }, [loadRules, loadDropdowns]);

  // ── sequence of execution (sort_order) ──────────────────────────
  // Canonical order = the backend's: sort_order ASC, then charge_name ASC.
  const compareBySequence = (a, b) =>
    ((Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0))
    || String(a.chargeName || '').localeCompare(String(b.chargeName || ''));
  const sortedIds = useMemo(
    () => [...rules].sort(compareBySequence).map((r) => r.id),
    [rules]
  );
  // Swap a rule with its neighbour in the sequence and persist the FULL order.
  const moveRule = useCallback(async (record, dir) => {
    const seq = [...rules].sort(compareBySequence);
    const idx = seq.findIndex((r) => r.id === (record && record.id));
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (idx < 0 || swap < 0 || swap >= seq.length) return;
    const next = seq.slice();
    [next[idx], next[swap]] = [next[swap], next[idx]];
    try {
      await otherChargesApi.reorderRules(next.map((r) => r.id));
      await loadRules();
      message.success('Charge sequence updated. Applies to new proformas.');
    } catch (err) {
      message.error(backendError(err, 'Failed to reorder charges.'));
    }
  }, [rules, loadRules]);

  // ── columns (Material-Type filter is master-driven) ─────────────
  const materialFilterOptions = useMemo(
    () => (dropdowns.materialTypes || []).map((o) => ({ text: o.label, value: o.code })),
    [dropdowns.materialTypes]
  );
  // Polish filter options for the grid's Polish column (master-driven, + ALL).
  const polishFilterOptions = useMemo(
    () => (dropdowns.polishTypes || []).map((o) => ({ text: o.label, value: o.code })),
    [dropdowns.polishTypes]
  );
  const columns = useMemo(
    () => buildOtherChargeColumns({ materialFilterOptions, polishFilterOptions, onMove: moveRule, sortedIds }),
    [materialFilterOptions, polishFilterOptions, moveRule, sortedIds]
  );

  // ── per-list loader state for the popup's async Selects ─────────
  const errFor = useCallback(
    (key) => (dropdowns.errors ? (dropdowns.errors.all || dropdowns.errors[key] || null) : null),
    [dropdowns.errors]
  );
  const dropdownState = useMemo(() => ({
    materialTypes: { loading: dropdownsLoading, error: errFor('materialTypes'), data: dropdowns.materialTypes },
    glassTypes:    { loading: dropdownsLoading, error: errFor('glassTypes'),    data: dropdowns.glassTypes },
    glassFamilies: { loading: dropdownsLoading, error: errFor('glassFamilies'), data: dropdowns.glassFamilies },
    thicknesses:   { loading: dropdownsLoading, error: errFor('thicknesses'),   data: dropdowns.thicknesses },
    categories:    { loading: dropdownsLoading, error: errFor('categories'),    data: dropdowns.categories },
    polishTypes:   { loading: dropdownsLoading, error: errFor('polishTypes'),   data: dropdowns.polishTypes }
  }), [dropdownsLoading, dropdowns, errFor]);

  // ── popup open / close ──────────────────────────────────────────
  const openAdd = useCallback(() => {
    setModalMode('create'); setModalRecord(null); setModalOpen(true);
  }, []);
  const openEdit = useCallback((record) => {
    setModalMode('edit'); setModalRecord(normalizeOtherChargeRule(record)); setModalOpen(true);
  }, []);
  const closeModal = useCallback(() => { setModalOpen(false); }, []);

  // ── popup submit (payload already built by the modal) ───────────
  const handleSubmit = useCallback(async (payload) => {
    setSubmitting(true);
    try {
      if (modalMode === 'edit' && modalRecord && modalRecord.id) {
        await otherChargesApi.updateRule(modalRecord.id, payload);
        message.success('Charge rule updated.');
      } else {
        await otherChargesApi.createRule(payload);
        message.success('Charge rule created.');
      }
      setModalOpen(false);
      await loadRules();
    } catch (err) {
      message.error(backendError(err, 'Failed to save charge rule.'));
    } finally {
      setSubmitting(false);
    }
  }, [modalMode, modalRecord, loadRules]);

  // ── delete (grid renders the confirm) ───────────────────────────
  const handleDelete = useCallback(async (record) => {
    try {
      await otherChargesApi.deleteRule(record.id);
      message.success('Charge rule deleted.');
      await loadRules();
    } catch (err) {
      message.error(backendError(err, 'Failed to delete charge rule.'));
    }
  }, [loadRules]);

  // ── shared static props ─────────────────────────────────────────
  const actionsProp = useMemo(() => ({ add: true, edit: true, delete: true }), []);
  const pageSizeOptions = useMemo(() => ['15', '25', '50', '100'], []);
  const searchableFields = useMemo(() => ['chargeCode', 'chargeName', 'hsnSac'], []);

  const contextMenuActions = useMemo(() => ([
    { key: 'edit',   label: 'Edit',   icon: <EditOutlined />,   onClick: openEdit },
    { key: 'delete', label: 'Delete', icon: <DeleteOutlined />, danger: true, onClick: handleDelete }
  ]), [openEdit, handleDelete]);

  return (
    <ErpMasterShell>
      <ErpDataGrid
        title="Other Charges Master"
        gridId="other-charges-master-v1"
        columns={columns}
        rowKey="id"
        dataSource={rules}
        loading={loading}
        onRefresh={loadRules}
        rowClassName={(record) => (record && record.active === false ? 'erp-grid__row--blocked' : '')}
        rowHeight="compact"
        stickyHeader
        selectable
        actionWidth={96}
        pageSizeOptions={pageSizeOptions}
        searchable
        searchableFields={searchableFields}
        searchPlaceholder="Search by code, name, HSN/SAC…"
        searchShortcut
        exportable
        exportFilename="other-charges-master"
        actions={actionsProp}
        addButtonText="Add Charge Rule"
        emptyTitle="No charge rules yet"
        emptyText="Add your first rule to apply surcharges & add-on charges automatically on documents."
        emptyCtaLabel="Add Charge Rule"
        onAdd={openAdd}
        onEdit={openEdit}
        onRowClick={openEdit}
        onDelete={handleDelete}
        contextMenuActions={contextMenuActions}
      />
      <OtherChargeRuleModal
        open={modalOpen}
        mode={modalMode}
        initialValues={modalRecord}
        dropdowns={dropdowns}
        dropdownState={dropdownState}
        onSubmit={handleSubmit}
        onCancel={closeModal}
        submitting={submitting}
      />
    </ErpMasterShell>
  );
}
